import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, isUserCancellationError, formatAuthErrorMessage } from '../context/AuthContext';
import { UserPlus, Loader2, Anchor, Calendar, MapPin, Clock, Key, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { INVITE_SYSTEM_ENABLED } from '../config';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import PasswordInput from '../components/PasswordInput';
import ConsentModal from '../components/ConsentModal';
import { hasValidConsent, createConsentData, getConsentByEmail, setConsentByEmail } from '../services/consentService';
import type { ConsentData } from '../services/consentService';
import { debug } from '../utils/debug';
import { saveCredentials } from '../utils/credentialManager';

interface SignupFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  inviteCode?: string;
}

const Signup = () => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignupFormData>();
  const { signup, loginWithGoogle, loginWithApple } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState<'google' | 'apple' | null>(null);
  const [authError, setAuthError] = useState('');
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<SignupFormData | null>(null);

  const handleGoogleSignup = async () => {
    try {
      setIsSocialLoading('google');
      setAuthError('');
      await loginWithGoogle();
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      if (!isUserCancellationError(error)) {
        setAuthError(formatAuthErrorMessage(error));
      }
    } finally {
      setIsSocialLoading(null);
    }
  };

  const handleAppleSignup = async () => {
    try {
      setIsSocialLoading('apple');
      setAuthError('');
      await loginWithApple();
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      if (!isUserCancellationError(error)) {
        setAuthError(formatAuthErrorMessage(error));
      }
    } finally {
      setIsSocialLoading(null);
    }
  };

  const password = watch('password');

  const handleFormSubmit = async (data: SignupFormData) => {
    debug.log('Form submitted with data:', data);
    
    // Check if user has already consented to cookies for this email
    const hasConsented = hasValidConsent(data.email);
    debug.log('Has consented for email:', data.email, '=', hasConsented);
    
    if (!hasConsented) {
      debug.log('No consent found, showing consent modal');
      // Show cookie consent modal and store form data
      setPendingFormData(data);
      setShowCookieConsent(true);
      return;
    }
    
    // Get existing consent data for this email
    const existingConsent = getConsentByEmail(data.email) as ConsentData;
    debug.log('Existing consent data:', existingConsent);
    
    if (existingConsent) {
      // Proceed with signup using existing consent
      debug.log('Proceeding with signup using existing consent');
      await processSignup(data, existingConsent);
    } else {
      debug.log('No existing consent found, this should not happen');
      setAuthError('Consent data not found. Please try again.');
    }
  };

  const processSignup = async (data: SignupFormData, consentData: ConsentData) => {
    debug.log('Processing signup with data:', data, 'and consent:', consentData);
    try {
      setIsLoading(true);
      setAuthError('');
      
      // Pass the invite code and consent data to the signup function
      debug.log('Calling signup function...');
      await signup(data.email, data.password, data.name, data.inviteCode, consentData);
      debug.log('Signup successful, navigating to dashboard');

      // Prompt to save credentials across Web & Native Mobile (runs in background so navigation is instant)
      saveCredentials({
        email: data.email,
        password: data.password,
        displayName: data.name,
      });

      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Signup error:', error);
      // The AuthContext will handle invite code validation and show appropriate toasts
      // We only need to handle general signup errors here
      if (error.code === 'auth/email-already-in-use') {
        setAuthError('This email is already in use. Please try another one.');
      } else if (error.message && error.message.includes('invite')) {
        setAuthError(error.message);
      } else if (error.message && error.message.includes('Consent is required')) {
        setAuthError('Consent is required to create an account.');
      } else {
        setAuthError('Failed to create an account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCookieConsent = (ageVerified: boolean, termsAccepted: boolean) => {
    debug.log('Consent response:', { ageVerified, termsAccepted });
    setShowCookieConsent(false);
    
    if (ageVerified && termsAccepted && pendingFormData) {
      debug.log('User consented with age verification and terms acceptance, proceeding with signup');
      // Create consent data and store it by email
      const consentData = createConsentData(true, pendingFormData.email, ageVerified, termsAccepted);
      debug.log('Created consent data:', consentData);
      
      // Store consent locally by email
      setConsentByEmail(pendingFormData.email, consentData);
      
      // Proceed with signup
      processSignup(pendingFormData, consentData);
      setPendingFormData(null);
    } else {
      debug.log('User did not complete consent requirements');
      // User didn't complete consent requirements - show error message
      setAuthError('You must verify your age and accept our terms of service and privacy policy to create an account.');
      setPendingFormData(null);
    }
  };

  return (
    <div className="flex relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(/images/photo-3601425.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'top',
          filter: 'brightness(0.7)'
        }}
      />

      {/* Content Container */}
      <div className="flex-1 flex pt-16">
        {/* Left Side - Features */}
        <motion.div 
          className="hidden lg:flex flex-col justify-center px-12 w-2/3 relative z-10"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-5xl font-bold text-white mb-6">
            Your Journey Starts Here
          </h1>
          <p className="text-xl text-white/90 mb-4">
            Join thousands of cruise enthusiasts who trust Cruising Along to organize their perfect vacation
          </p>
          
          {INVITE_SYSTEM_ENABLED && (
            <div className="bg-primary-600/75 backdrop-blur-sm rounded-lg p-4 mb-8">
              <div className="flex items-center mb-2">
                <Key className="h-5 w-5 text-accent-400 mr-2" />
                <span className="text-accent-400 font-semibold">Beta Testing</span>
              </div>
              <p className="text-white/90 text-sm">
                We're currently in beta! You'll need an invite code to create an account. 
                This helps us ensure the best experience as we perfect the platform.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-8">
            <motion.div 
              className="bg-primary-600/75 backdrop-blur-sm rounded-lg p-6 shadow-lg"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Calendar className="h-8 w-8 text-secondary-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Track Your Cruises</h3>
              <p className="text-white/80">
                Organize all your upcoming cruises in one place. Never miss a sailing date again.
              </p>
            </motion.div>

            <motion.div 
              className="bg-primary-600/75 backdrop-blur-sm rounded-lg p-6 shadow-lg"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <MapPin className="h-8 w-8 text-secondary-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Port Explorer</h3>
              <p className="text-white/80">
                Plan your shore excursions and keep track of every destination on your itinerary.
              </p>
            </motion.div>

            <motion.div 
              className="bg-primary-600/75 backdrop-blur-sm rounded-lg p-6 shadow-lg"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Clock className="h-8 w-8 text-secondary-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Sailing Stats</h3>
              <p className="text-white/80">
                See your total nights at sea, ports visited, and cruise lines sailed with.
              </p>
            </motion.div>

            <motion.div 
              className="bg-primary-600/75 backdrop-blur-sm rounded-lg p-6 shadow-lg"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Anchor className="h-8 w-8 text-secondary-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Travel Details</h3>
              <p className="text-white/80">
                Manage flights, hotels, and transportation all in one convenient location.
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Side - Signup Form */}
        <motion.div 
          className="w-full lg:w-1/3 flex items-center justify-center p-8 relative z-10"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-primary-600/75 backdrop-blur-sm rounded-xl p-8 w-full max-w-md shadow-2xl">
            <div className="text-center mb-8">
              <CustomCruiseIcon size={82} className="text-accent mx-auto" />
              <h1 className="text-2xl font-bold text-white mt-4">
                {INVITE_SYSTEM_ENABLED ? 'Join the Beta' : 'Create Your Account'}
              </h1>
              <p className="text-white/80 mt-2">
                {INVITE_SYSTEM_ENABLED 
                  ? 'Enter your invite code to get started' 
                  : 'Start planning your next cruise adventure'
                }
              </p>
            </div>

            {authError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 w-full max-w-full overflow-hidden">
                <div className="flex items-start min-w-0 w-full">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-red-800 text-sm break-words [overflow-wrap:anywhere] min-w-0 flex-1">{authError}</p>
                </div>
              </div>
            )}

            {/* Social Logins */}
            <div className="flex flex-col space-y-3 mb-6">
              <button
                type="button"
                onClick={handleGoogleSignup}
                disabled={isSocialLoading !== null || isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-medium text-sm transition-all duration-200 shadow-sm"
              >
                {isSocialLoading === 'google' ? (
                  <Loader2 className="h-4 w-4 animate-spin text-gray-600" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                )}
                <span>Sign in with Google</span>
              </button>

              <button
                type="button"
                onClick={handleAppleSignup}
                disabled={isSocialLoading !== null || isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white rounded-lg hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-medium text-sm transition-all duration-200 shadow-sm border border-white/10"
              >
                {isSocialLoading === 'apple' ? (
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                ) : (
                  <svg className="h-5 w-auto fill-current" viewBox="0 0 384 512">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z"/>
                  </svg>
                )}
                <span>Sign in with Apple</span>
              </button>

              <p className="text-[11px] text-white/70 text-center leading-tight pt-1">
                Already have a Cruising Along account? Log into your primary account first, then connect Google or Apple under Profile Settings.
              </p>
            </div>

            {/* Social Sign Up Divider */}
            <div className="flex items-center my-6">
              <div className="flex-grow border-t border-white/20" />
              <span className="flex-shrink mx-3 text-xs font-semibold uppercase tracking-wider text-white/70">
                Or sign up with email
              </span>
              <div className="flex-grow border-t border-white/20" />
            </div>

            <form method="post" action="/signup" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
              {INVITE_SYSTEM_ENABLED && (
                <div>
                  <label htmlFor="inviteCode" className="block text-sm font-medium text-white mb-1">
                    <div className="flex items-center">
                      <Key className="h-4 w-4 mr-1" />
                      Invite Code *
                    </div>
                  </label>
                  <input
                    id="inviteCode"
                    type="text"
                    {...register('inviteCode', { 
                      required: INVITE_SYSTEM_ENABLED ? 'Invite code is required' : false 
                    })}
                    className={`w-full px-3 py-2 bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 ${
                      errors.inviteCode ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your beta invite code"
                  />
                  {errors.inviteCode && (
                    <p className="mt-1 text-sm text-red-400">{errors.inviteCode.message}</p>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white mb-1">
                  Full Name
                </label>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  {...register('name', { required: 'Name is required' })}
                  className={`w-full px-3 py-2 bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 ${
                    errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="John Doe"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className={`w-full px-3 py-2 bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 ${
                    errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white mb-1">
                  Password
                </label>
                <PasswordInput
                  id="password"
                  autoComplete="new-password"
                  register={register('password', { 
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  error={!!errors.password}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white mb-1">
                  Confirm Password
                </label>
                <PasswordInput
                  id="confirmPassword"
                  autoComplete="new-password"
                  register={register('confirmPassword', { 
                    required: 'Please confirm your password',
                    validate: value => value === password || 'Passwords do not match'
                  })}
                  error={!!errors.confirmPassword}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-400">{errors.confirmPassword.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-10 flex justify-center items-center bg-secondary-600 hover:bg-secondary-500 text-white py-2 px-4 rounded-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-opacity-50 font-medium"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Signing up...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-5 w-5 mr-2" />
                    {INVITE_SYSTEM_ENABLED ? 'Join Beta' : 'Create Account'}
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-white">
                Already have an account?{' '}
                <Link to="/login" className="text-secondary-400 hover:text-secondary-300 font-medium transition-colors duration-200">
                  Log in
                </Link>
              </p>
            </div>

            <div className="mt-4 text-center text-sm text-white/60">
              <p>
                By creating an account, you agree to our{' '}
                <a 
                  href="https://www.cruisingalong.com/terms-of-service.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-secondary-400 hover:text-secondary-300 transition-colors duration-200"
                >
                  App Terms of Service
                </a>
                {' '}and{' '}
                <a 
                  href="https://www.cruisingalong.com/privacy-policy.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-secondary-400 hover:text-secondary-300 transition-colors duration-200"
                >
                  Privacy Policy
                </a>
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Terms & Privacy Policy Consent Modal */}
      <ConsentModal
        isOpen={showCookieConsent}
        onAccept={handleCookieConsent}
        onDecline={() => handleCookieConsent(false, false)}
        userEmail={pendingFormData?.email}
      />
    </div>
  );
};

export default Signup;